import { AssetSize } from '../types';

interface SizeOption {
  id: AssetSize;
  label: string;
  dims: string;
  icon: string;
  ratio: string;
}

const SIZES: SizeOption[] = [
  { id: 'square', label: 'Square', dims: '1080 × 1080', icon: 'ri-layout-grid-2-line', ratio: '1:1' },
  { id: 'portrait', label: 'Portrait', dims: '1080 × 1350', icon: 'ri-phone-fill', ratio: '4:5' },
  { id: 'story', label: 'Story / Reel', dims: '1080 × 1920', icon: 'ri-smartphone-line', ratio: '9:16' },
  { id: 'landscape', label: 'Landscape', dims: '1200 × 628', icon: 'ri-computer-line', ratio: '1.91:1' },
];

interface AssetVariationsProps {
  selected: AssetSize[];
  autoAdapt: boolean;
  onSizeChange: (sizes: AssetSize[]) => void;
  onAutoAdaptChange: (v: boolean) => void;
}

export default function AssetVariations({ selected, autoAdapt, onSizeChange, onAutoAdaptChange }: AssetVariationsProps) {
  const toggle = (id: AssetSize) => {
    onSizeChange(selected.includes(id) ? selected.filter((s) => s !== id) : [...selected, id]);
  };

  return (
    <div className="bg-stone-900 border border-stone-800 rounded-xl p-5 mb-4">
      <h3 className="text-sm font-semibold text-white mb-4">Asset Variations</h3>
      <div className="grid grid-cols-2 gap-2 mb-4">
        {SIZES.map((s) => {
          const isActive = selected.includes(s.id);
          return (
            <button
              key={s.id}
              onClick={() => toggle(s.id)}
              className={`flex items-center gap-3 px-3 py-3 rounded-xl border text-left transition-all duration-200 cursor-pointer ${
                isActive
                  ? 'bg-orange-500/10 border-orange-500/60 ring-1 ring-orange-500/30'
                  : 'bg-stone-800/40 border-stone-700/60 hover:border-stone-600'
              }`}
            >
              <div className={`w-8 h-8 flex items-center justify-center rounded-lg shrink-0 ${isActive ? 'bg-orange-500/20' : 'bg-stone-800'}`}>
                <i className={`${s.icon} text-sm ${isActive ? 'text-orange-400' : 'text-stone-400'}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className={`text-xs font-semibold ${isActive ? 'text-white' : 'text-stone-300'}`}>{s.label}</div>
                <div className="text-xs text-stone-600">{s.dims}</div>
              </div>
              <span className={`text-xs px-1.5 py-0.5 rounded font-mono shrink-0 ${isActive ? 'bg-orange-500/20 text-orange-400' : 'bg-stone-800 text-stone-600'}`}>
                {s.ratio}
              </span>
            </button>
          );
        })}
      </div>

      <div className={`flex items-start gap-3 px-4 py-3.5 rounded-xl border transition-all duration-200 cursor-pointer ${
        autoAdapt ? 'bg-orange-500/10 border-orange-500/40' : 'bg-stone-800/40 border-stone-700/60'
      }`}
        onClick={() => onAutoAdaptChange(!autoAdapt)}
      >
        <div className={`w-10 h-6 rounded-full relative transition-all duration-300 shrink-0 mt-0.5 ${autoAdapt ? 'bg-orange-500' : 'bg-stone-700'}`}>
          <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all duration-300 ${autoAdapt ? 'left-[18px]' : 'left-0.5'}`} />
        </div>
        <div>
          <div className={`text-sm font-semibold mb-0.5 ${autoAdapt ? 'text-white' : 'text-stone-300'}`}>Auto-adapt Layout</div>
          <p className="text-xs text-stone-500 leading-relaxed">
            Automatically reposition and resize text, buttons, and imagery for each platform size.
          </p>
        </div>
      </div>
    </div>
  );
}
